import { createStore } from 'vuex'
import { authAPI, userAPI, writersAPI, articlesAPI, APIError } from '../api'
import type { UserDetail, Writer, ArticleDetail } from '../api/types'

interface State {
  // User state
  user: UserDetail | null
  isLoading: boolean
  isInitializing: boolean
  error: string | null

  // Onboarding state
  showOnboardingModal: boolean
  onboardingStep: 1 | 2 | 3

  // Writers state
  writers: Writer[]
  writersLoading: boolean

  // Article state
  currentArticle: ArticleDetail | null
  articleLoading: boolean
}

export default createStore<State>({
  state: {
    user: null,
    isLoading: false,
    isInitializing: true,
    error: null,

    showOnboardingModal: false,
    onboardingStep: 1,

    writers: [],
    writersLoading: false,

    currentArticle: null,
    articleLoading: false,
  },

  getters: {
    isAuthenticated: (state) => !!state.user,
    currentUser: (state) => state.user,
    isOnboardingComplete: (state) => state.user?.is_onboarding_complete ?? false,
    hasWriters: (state) => state.writers.length > 0,
  },

  mutations: {
    SET_USER(state, user: UserDetail | null) {
      state.user = user
    },
    SET_LOADING(state, isLoading: boolean) {
      state.isLoading = isLoading
    },
    SET_INITIALIZING(state, isInitializing: boolean) {
      state.isInitializing = isInitializing
    },
    SET_ERROR(state, error: string | null) {
      state.error = error
    },

    // Onboarding mutations
    SET_SHOW_ONBOARDING_MODAL(state, show: boolean) {
      state.showOnboardingModal = show
    },
    SET_ONBOARDING_STEP(state, step: 1 | 2 | 3) {
      state.onboardingStep = step
    },

    // Writers mutations
    SET_WRITERS(state, writers: Writer[]) {
      state.writers = writers
    },
    SET_WRITERS_LOADING(state, loading: boolean) {
      state.writersLoading = loading
    },
    ADD_WRITER(state, writer: Writer) {
      state.writers.unshift(writer)
    },
    UPDATE_WRITER(state, writer: Writer) {
      const index = state.writers.findIndex((w) => w.id === writer.id)
      if (index !== -1) {
        state.writers.splice(index, 1, writer)
      }
    },
    REMOVE_WRITER(state, writerId: string) {
      state.writers = state.writers.filter((w) => w.id !== writerId)
    },

    // Article mutations
    SET_CURRENT_ARTICLE(state, article: ArticleDetail | null) {
      state.currentArticle = article
    },
    SET_ARTICLE_LOADING(state, loading: boolean) {
      state.articleLoading = loading
    },
  },

  actions: {
    // Auth actions
    async signup({ commit, dispatch }, { email, password }: { email: string; password: string }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        await authAPI.signup(email, password)
        await dispatch('fetchUser')
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async signin({ commit, dispatch }, { email, password }: { email: string; password: string }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        await authAPI.signin(email, password)
        await dispatch('fetchUser')
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async signout({ commit }) {
      try {
        await authAPI.signout()
      } finally {
        commit('SET_USER', null)
        commit('SET_WRITERS', [])
        commit('SET_CURRENT_ARTICLE', null)
      }
    },

    async fetchUser({ commit, state }) {
      // Set initializing only if user is not yet loaded (initial load)
      const isInitialLoad = state.user === null && state.isInitializing
      if (isInitialLoad) {
        commit('SET_INITIALIZING', true)
      }
      try {
        const user = await userAPI.getMe()
        commit('SET_USER', user)
      } catch (error) {
        // Don't show error for 401 when fetching user (user just not logged in)
        if (error instanceof APIError && error.statusCode === 401) {
          commit('SET_USER', null)
        } else {
          // Don't show error for network errors when fetching user
          if (error instanceof APIError && error.statusCode !== 401) {
            commit('SET_ERROR', error.message)
          }
          commit('SET_USER', null)
        }
      } finally {
        if (isInitialLoad) {
          commit('SET_INITIALIZING', false)
        }
      }
    },

    // Onboarding actions
    checkOnboarding({ commit, state }) {
      if (state.user && !state.user.is_onboarding_complete) {
        // Determine the correct step based on existing user data
        let initialStep: 1 | 2 | 3 = 1
        if (state.user.about_business) {
          initialStep = 2
          if (state.user.fields_of_interest && state.user.fields_of_interest.length > 0) {
            initialStep = 3
          }
        }
        commit('SET_SHOW_ONBOARDING_MODAL', true)
        commit('SET_ONBOARDING_STEP', initialStep)
      }
    },

    async completeOnboarding(
      { commit },
      data: { about_business?: string; business_url?: string; fields_of_interest: string[]; keywords_to_monitor: string[] }
    ) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const user = await userAPI.completeOnboarding(data)
        commit('SET_USER', user)
        // After onboarding steps 1+2, move to step 3 (writer creation)
        commit('SET_ONBOARDING_STEP', 3)
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async completeOnboardingWriter(
      { commit, dispatch },
      data: { name: string; tone_of_voice: string; style_guide: string; speech_pattern_guidelines?: string }
    ) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const writer = await userAPI.completeOnboardingWriter(data)
        commit('ADD_WRITER', writer)
        // Refresh user to get updated onboarding status
        await dispatch('fetchUser')
        // Close onboarding modal
        commit('SET_SHOW_ONBOARDING_MODAL', false)
        commit('SET_ONBOARDING_STEP', 1)
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Writers actions
    async fetchWriters({ commit }) {
      commit('SET_WRITERS_LOADING', true)
      try {
        const writers = await writersAPI.list()
        commit('SET_WRITERS', writers)
      } catch (error) {
        if (error instanceof APIError) {
          commit('SET_ERROR', error.message)
        }
      } finally {
        commit('SET_WRITERS_LOADING', false)
      }
    },

    async createWriter({ commit }, data: { name: string; tone_of_voice: string; style_guide: string; ui_emoji?: string; speech_pattern_guidelines?: string }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const writer = await writersAPI.create(data)
        commit('ADD_WRITER', writer)
        return writer
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateWriter({ commit }, { writerId, data }: { writerId: string; data: Partial<Writer> }) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const writer = await writersAPI.update(writerId, data)
        commit('UPDATE_WRITER', writer)
        return writer
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deleteWriter({ commit }, writerId: string) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        await writersAPI.delete(writerId)
        commit('REMOVE_WRITER', writerId)
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    // Article actions
    async createArticle({ commit }, data: { writer_id: string; general_instructions: string; word_count_from?: number; word_count_to?: number; keywords?: string[]; generate_schema_org_schemas?: boolean; include_citations?: boolean; call_to_action?: string }) {
      commit('SET_ARTICLE_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const article = await articlesAPI.create(data)
        commit('SET_CURRENT_ARTICLE', article)
        return article
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_ARTICLE_LOADING', false)
      }
    },

    async fetchArticle({ commit }, articleId: string) {
      commit('SET_ARTICLE_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const article = await articlesAPI.get(articleId)
        commit('SET_CURRENT_ARTICLE', article)
        return article
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_ARTICLE_LOADING', false)
      }
    },

    async updateArticle({ commit }, { articleId, data }: { articleId: string; data: Partial<ArticleDetail> }) {
      commit('SET_ARTICLE_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const article = await articlesAPI.update(articleId, data)
        commit('SET_CURRENT_ARTICLE', article)
        return article
      } catch (error) {
        const errorMessage = error instanceof APIError ? error.message : 'An unexpected error occurred'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_ARTICLE_LOADING', false)
      }
    },

    clearError({ commit }) {
      commit('SET_ERROR', null)
    },
  },
})
